import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  selectUserHasAccess,
  selectUserHasValidTicket,
  selectUserHasAccessViaPPC,
  selectShouldDisableTicketPurchase,
  selectShouldShowPrice,
  selectShouldShowVideo,
  selectUserCanPurchaseEvent,
} from '../selectors/livestreamEvents';
import { selectUserIsAuthenticated } from '../selectors/user';
import * as meActions from '../actions/me';
import * as uiActions from '../actions/ui';
import * as analyticsActions from '../actions/analytics';
import * as collectionActions from '../actions/collection';
import { ENTITY_TYPE_LIVESTREAM_EVENT, PLAN_ID_PPC_12M } from '../constants';
import { selectUserCanAccessSubscribeView } from '../selectors/subscription';
import { selectIsInApp } from '../selectors/client';
import { FormattedMessage } from 'react-intl';

export const livestreamEventComponentPropTypes = {
  userHasAccessViaPPC: PropTypes.bool.isRequired,
  userHasTicket: PropTypes.bool.isRequired,
  userHasAccess: PropTypes.bool.isRequired,
  ticketPurchaseIsDisabled: PropTypes.bool.isRequired,
  isAuth: PropTypes.bool.isRequired,
  shouldShowPrice: PropTypes.bool.isRequired,
  shouldShowVideo: PropTypes.bool.isRequired,
  userCanPurchaseEvent: PropTypes.bool.isRequired,
  userCanAccessSubscribeView: PropTypes.bool.isRequired,
  track: PropTypes.func.isRequired,
  isInApp: PropTypes.bool.isRequired,
};

function mapStateToProps(state, ownProps) {
  const { event } = ownProps;

  return {
    userHasAccessViaPPC: selectUserHasAccessViaPPC(state, event),
    userHasTicket: selectUserHasValidTicket(state, event),
    userHasAccess: selectUserHasAccess(state, event),
    ticketPurchaseIsDisabled: selectShouldDisableTicketPurchase(state, event),
    isAuth: selectUserIsAuthenticated(state),
    shouldShowPrice: selectShouldShowPrice(state, event),
    shouldShowVideo: selectShouldShowVideo(state, event),
    userCanPurchaseEvent: selectUserCanPurchaseEvent(state, event),
    userCanAccessSubscribeView: selectUserCanAccessSubscribeView(state),
    isInApp: selectIsInApp(state),
  };
}

function getDisplayName(component) {
  return component.displayName || component.name || 'Component';
}

export default function livestreamEventComponent(options = {}) {
  const { withRef = false } = options;

  return WrappedComponent => {
    class LivestreamEventComponent extends Component {
      static propTypes = livestreamEventComponentPropTypes;

      onOrderComplete = order => {
        const { onOrderComplete, event } = this.props;
        if (onOrderComplete) {
          // Track it
          onOrderComplete(order, event);
        }

        // Reload the user object
        this.props.loadMe();
        this.props.toggleInCollection(ENTITY_TYPE_LIVESTREAM_EVENT, event.id, 'Livestream Event');
      };

      onPurchaseClick = () => {
        const { contextType, track } = this.props;
        const { setFromIdIfNoOffer, showModal, event } = this.props;

        setFromIdIfNoOffer(event.isCourse ? 'course-ticket-purchase' : 'gch-ticket-purchase');
        showModal(
          'FREE_EXPERIENCE_PREVIEWS_MODAL',
          { trigger: 'livestreamEvent' },
          {
            trigger: 'livestreamEventFreePreviewsModal',
            title: (
              <FormattedMessage
                id="livestream-event-buy-ticket-previews-modal.header"
                defaultMessage="Want to see this event?"
              />
            ),
            subtitle: (
              <FormattedMessage
                id="livestream-event-buy-ticket-previews-modal.subtitle"
                defaultMessage="Create a free account to buy a ticket."
              />
            ),
          }
        );

        track('Clicked CTA Button: Ticket Purchase', {
          contextType: contextType,
          contextId: event.slug,
          liveEvent: event.slug,
          liveStreamEventType: event.type,
        });
      };

      onSignupClick = () => {
        const { setFromIdIfNoOffer, showModal, track, event } = this.props;

        setFromIdIfNoOffer('event-free-access');
        showModal(
          'FREE_EXPERIENCE_PREVIEWS_MODAL',
          { trigger: 'livestreamEvent' },
          {
            trigger: 'livestreamEventFreePreviewsModal',
            title: (
              <FormattedMessage
                id="livestream-event-free-event-previews-modal.header"
                defaultMessage="Want to see this free event?"
              />
            ),
            subtitle: (
              <FormattedMessage
                id="livestream-event-free-event-previews-modal.subtitle"
                defaultMessage="Create a free account to watch."
              />
            ),
          }
        );

        track('Clicked CTA Button: Create Account', {
          contextType: 'livestreamEvent',
          contextId: event.slug,
          liveEvent: event.slug,
          liveStreamEventType: event.type,
        });
      };

      onSubscribeClick = () => {
        const { track, event, showModal } = this.props;

        showModal(
          'PREMIUM_EXPERIENCE_PREVIEWS_MODAL',
          { trigger: 'livestreamEvent' },
          {
            trigger: 'livestreamEventPremiumPreviewsModal',
            plan: PLAN_ID_PPC_12M,
            title: (
              <FormattedMessage
                id="livestream-event-premium-previews-modal.header"
                defaultMessage="Want to see all concerts?"
              />
            ),
            subtitle: (
              <FormattedMessage
                id="livestream-event-premium-previews-modal.subtitle"
                defaultMessage="Subscribe to get unlimited access to all concerts."
              />
            ),
          }
        );

        track('Clicked CTA Button: Subscribe', {
          contextType: 'livestreamEvent',
          contextId: event.slug,
          liveEvent: event.slug,
          liveStreamEventType: event.type,
        });
      };

      render() {
        return (
          <WrappedComponent
            {...this.props}
            onOrderComplete={this.onOrderComplete}
            onPurchaseClick={this.onPurchaseClick}
            onSubscribeClick={this.onSubscribeClick}
            onSignupClick={this.onSignupClick}
            ref={withRef ? 'wrappedInstance' : null}
          />
        );
      }
    }

    LivestreamEventComponent.displayName = `LivestreamEventComponent(${getDisplayName(
      WrappedComponent
    )}`;

    LivestreamEventComponent.WrappedComponent = WrappedComponent;

    return connect(mapStateToProps, {
      showModal: uiActions.showModal,
      track: analyticsActions.track,
      loadMe: meActions.loadMe,
      toggleInCollection: collectionActions.toggleInCollection,
      setFromIdIfNoOffer: meActions.setFromIdIfNoOffer,
    })(LivestreamEventComponent);
  };
}
